import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { annotationsAPI } from "./api";
import { NOT_TAGGED } from "../../pages/app/utils/constants";

// Utility functions for region management
const compareFloat = (a, b) => {
  if (Math.abs(a - b) < 0.00001) return 0;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

const regionSortComparator = (a, b) => {
  // Sort by tag first
  if (a.tag !== b.tag) {
    return ("" + a.tag).localeCompare("" + b.tag);
  }
  const startDateCompare = compareFloat(a.start, b.start);
  if (startDateCompare === 0) {
    return compareFloat(a.end, b.end);
  } else {
    return startDateCompare;
  }
};

/**
 * Clean up regions - remove duplicates and merge overlapping regions with same tag
 */
export const cleanUpRegions = (regions) => {
  if (!regions || regions.length === 0) return [];

  // Sort by tag and by start then end date
  const sortedRegions = cloneDeep(regions).sort(regionSortComparator);
  const result = [];
  let lastRegion = undefined;

  for (let r of sortedRegions) {
    if (lastRegion === undefined) {
      lastRegion = cloneDeep(r);
      continue;
    }

    // Add the last region if a new tag is discovered
    if (r.tag !== lastRegion.tag) {
      result.push(lastRegion);
      lastRegion = cloneDeep(r);
      continue;
    }

    // Push if a new region if there is no overlap (the new one starts after the last one ends)
    if (r.start > lastRegion.end) {
      result.push(lastRegion);
      lastRegion = cloneDeep(r);
      continue;
    }

    // Merge overlapping regions with same tag
    if (r.start <= lastRegion.end) {
      if (r.start < lastRegion.start) lastRegion.start = r.start;
      if (r.end > lastRegion.end) lastRegion.end = r.end;
      continue; // Discards "r" because it's merged in with the last region
    }
  }

  // Push the last one in the list
  if (lastRegion) {
    result.push(lastRegion);
  }

  return result;
};

/**
 * Extract unique tags from annotations
 */
const extractTagList = (annotations) => {
  const tagList = [];
  annotations.forEach((annotation) => {
    if (
      annotation.tag &&
      annotation.tag !== NOT_TAGGED &&
      !tagList.includes(annotation.tag)
    ) {
      tagList.push(annotation.tag);
    }
  });
  return tagList.sort((a, b) => a.localeCompare(b));
};

// Initial state
const initialState = {
  annotations: [],
  tagList: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastSyncTime: null,
  tagUpdateNumber: 0,
};

/**
 * Load annotations from server
 */
export const loadAnnotations = createAsyncThunk(
  "annotations/loadAnnotations",
  async ({ caseId, clipId }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;

    try {
      const result = await annotationsAPI.loadAnnotations(
        accessCode,
        caseId,
        clipId,
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to load annotations",
        });
      }

      const annotations = result.data || [];
      const tagList = extractTagList(annotations);

      return {
        annotations,
        tagList,
      };
    } catch (error) {
      console.error("[annotations/loadAnnotations] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to load annotations",
      });
    }
  },
);

/**
 * Save annotations to server
 */
export const saveAnnotations = createAsyncThunk(
  "annotations/saveAnnotations",
  async (
    { caseId, clipId, annotations, untaggedUpdate },
    { getState, rejectWithValue },
  ) => {
    const { accessCode } = getState().auth;

    try {
      // Clean up annotations before saving
      const cleanedAnnotations = cleanUpRegions(annotations).filter(
        (a) => a.tag !== undefined && a.tag !== NOT_TAGGED,
      );
      const untaggedAnnotations = annotations.filter(
        (a) => !a.tag || a.tag === NOT_TAGGED,
      );

      if (untaggedUpdate) {
        // don't save to server
        return {
          annotations: [...cleanedAnnotations, ...untaggedAnnotations],
          tagList: extractTagList([
            ...cleanedAnnotations,
            ...untaggedAnnotations,
          ]),
        };
      }

      const result = await annotationsAPI.saveAnnotations(
        accessCode,
        caseId,
        clipId,
        cleanedAnnotations,
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to save annotations",
        });
      }

      console.log(`[annotations/saveAnnotations] Saved`, result.data);
      const savedAnnotations = [...result.data, ...untaggedAnnotations];
      const tagList = extractTagList(savedAnnotations);

      return {
        annotations: savedAnnotations,
        tagList,
      };
    } catch (error) {
      console.error("[annotations/saveAnnotations] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to save annotations",
      });
    }
  },
);

/**
 * Delete annotation by ID
 */
export const deleteAnnotation = createAsyncThunk(
  "annotations/deleteAnnotation",
  async ({ caseId, clipId, annotationId }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;
    const { annotations } = getState().annotations;

    try {
      const removedAnnotation = annotations.find((a) => a.id === annotationId);
      const updatedAnnotations = annotations.filter(
        (a) => a.id !== annotationId,
      );
      if (removedAnnotation.tag && removedAnnotation.tag !== NOT_TAGGED) {
        const result = await annotationsAPI.saveAnnotations(
          accessCode,
          caseId,
          clipId,
          updatedAnnotations,
        );

        if (!result.success) {
          return rejectWithValue({
            error: result.error || "Failed to delete annotation",
          });
        }
      }

      return {
        annotations: updatedAnnotations,
        tagList: extractTagList(updatedAnnotations),
      };
    } catch (error) {
      console.error("[annotations/deleteAnnotation] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to delete annotation",
      });
    }
  },
);

export const tagAnnotation = createAsyncThunk(
  "annotations/tagAnnotation",
  async ({ caseId, clipId, tag }, { getState, rejectWithValue }) => {
    const { accessCode } = getState().auth;
    const { annotations, tagList } = getState().annotations;

    if (
      tag &&
      tagList.length >= 10 &&
      !tagList.includes(tag.trim().toUpperCase())
    ) {
      return rejectWithValue({
        error:
          "Tag limit reached. Please remove an existing tag before adding a new one.",
      });
    }

    try {
      // Update all annotations with empty tags to the new tag
      const updatedAnnotations = annotations.map((annotation) => {
        if (annotation.tag === NOT_TAGGED || annotation.tag === undefined) {
          return { ...annotation, tag: tag.trim().toUpperCase() };
        }
        return annotation;
      });
      console.log(
        `[annotations/tagAnnotation] Tagging ${updatedAnnotations.length} annotations with tag "${tag}"`,
        updatedAnnotations,
      );

      // Clean up annotations before saving
      const cleanedAnnotations = cleanUpRegions(updatedAnnotations);

      // Save to server
      const result = await annotationsAPI.saveAnnotations(
        accessCode,
        caseId,
        clipId,
        cleanedAnnotations,
      );

      if (!result.success) {
        return rejectWithValue({
          error: result.error || "Failed to tag annotation",
        });
      }

      const newTagList = extractTagList(cleanedAnnotations);

      return {
        tag: tag.trim().toUpperCase(),
        annotations: cleanedAnnotations,
        tagList: newTagList,
      };
    } catch (error) {
      console.error("[annotations/tagAnnotation] Error:", error);
      return rejectWithValue({
        error: error.message || "Failed to tag annotation",
      });
    }
  },
);

// Create the slice
const annotationsSlice = createSlice({
  name: "annotations",
  initialState,
  reducers: {
    // Reset to initial state
    reset: () => cloneDeep(initialState),

    // Add annotation locally (optimistic update)
    addAnnotation: (state, action) => {
      const newAnnotation = {
        id: action.payload.id,
        start: action.payload.start,
        end: action.payload.end,
        tag: action.payload.tag || NOT_TAGGED,
        ...action.payload,
      };

      // Check if annotation already exists
      const existingIndex = state.annotations.findIndex(
        (a) => a.id === newAnnotation.id,
      );
      if (existingIndex === -1) {
        state.annotations.push(newAnnotation);

        // Update tag list if new tag
        if (
          newAnnotation.tag &&
          newAnnotation.tag !== NOT_TAGGED &&
          !state.tagList.includes(newAnnotation.tag)
        ) {
          state.tagList.push(newAnnotation.tag);
        }
      }
    },

    // Update annotation locally (optimistic update)
    updateAnnotation: (state, action) => {
      const { id, start, end, tag, ...otherProps } = action.payload;
      const existingIndex = state.annotations.findIndex((a) => a.id === id);

      if (existingIndex !== -1) {
        state.annotations[existingIndex] = {
          ...state.annotations[existingIndex],
          start,
          end,
          tag: tag !== undefined ? tag : state.annotations[existingIndex].tag,
          ...otherProps,
        };

        // Update tag list if new tag
        if (tag && tag !== NOT_TAGGED && !state.tagList.includes(tag)) {
          state.tagList.push(tag);
        }
      }
    },

    loadSNGAnnotations: (state) => {
      state.annotations = [{ id: "sng", start: 0, end: 2, tag: NOT_TAGGED }];
      state.tagList = [];
      state.isLoading = false;
      state.error = null;
      state.lastSyncTime = Date.now();
    },

    // Clear error state
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Load annotations
      .addCase(loadAnnotations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadAnnotations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.annotations = action.payload.annotations;
        state.tagList = action.payload.tagList;
        state.lastSyncTime = Date.now();
      })
      .addCase(loadAnnotations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || "Failed to load annotations";
      })
      // Save annotations
      .addCase(saveAnnotations.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveAnnotations.fulfilled, (state, action) => {
        state.isSaving = false;
        state.annotations = action.payload.annotations;
        state.tagList = action.payload.tagList;
        state.lastSyncTime = Date.now();
      })
      .addCase(saveAnnotations.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.error || "Failed to save annotations";
      })
      // Tag annotation
      .addCase(tagAnnotation.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(tagAnnotation.fulfilled, (state, action) => {
        state.isSaving = false;
        state.annotations = action.payload.annotations;
        state.tagList = action.payload.tagList;
        state.lastSyncTime = Date.now();
        state.tagUpdateNumber += 1;
      })
      .addCase(tagAnnotation.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.error || "Failed to tag annotation";
      })
      // Delete annotation
      .addCase(deleteAnnotation.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteAnnotation.fulfilled, (state, action) => {
        state.isSaving = false;
        state.annotations = action.payload.annotations;
        state.tagList = extractTagList(state.annotations);
        state.lastSyncTime = Date.now();
      })
      .addCase(deleteAnnotation.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.error || "Failed to delete annotation";
      });
  },
});

// Export actions
export const {
  reset,
  loadSNGAnnotations,
  addAnnotation,
  updateAnnotation,
  clearError,
} = annotationsSlice.actions;

// Export reducer
export default annotationsSlice.reducer;
